// src/components/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import EventManagement from './admin/EventManagement';
import DonorManagementSystem from './admin/DonorManagementSystem';
import UserManagement from './admin/UserManagement';
import VolunteerApplications from './VolunteerApplications';
import MemberManagement from './MemberManagement';
import PendingPaymentsManagement from './admin/PendingPaymentsManagement';
import SimpleCourseManagement from './SimpleCourseManagement';
import LeadershipManagement from './admin/LeadershipManagement';
import CollaboratorsManagement from './admin/CollaboratorsManagement';
import ChaptersManagement from './admin/ChaptersManagement';
import InstitutionsManagement from './admin/InstitutionsManagement';
import YouTubeVideosManagement from './admin/YouTubeVideosManagement';
import NewsManagement from './admin/NewsManagement';
import CommunityManagement from './admin/CommunityManagement';
import NewsletterManagement from './admin/NewsletterManagement';
import LeaderReportsManagement from './admin/LeaderReportsManagement';
import StoreManagement from './admin/StoreManagement';
import ContactSubmissions from './ContactSubmissions';
import BulkMessaging from './admin/BulkMessaging';
import SettingsManagement from './admin/SettingsManagement';
import AdvertisementManagement from './admin/AdvertisementManagement';
import MediaKitManagement from './admin/MediaKitManagement';
import PhotoGalleryManagement from './admin/PhotoGalleryManagement';
import CountryFlagsManagement from './admin/CountryFlagsManagement';
import ImpactManagement from './admin/ImpactManagement';
import ResourcesManagement from './admin/ResourcesManagement';
import VendorManagement from './admin/VendorManagement';
import SurveyManagement from './admin/SurveyManagement';
import Arca2027SurveyResponses from './admin/Arca2027SurveyResponses';
import CpdRegistrationsManagement from './admin/CpdRegistrationsManagement';
import CrceRegistrationsManagement from './admin/CrceRegistrationsManagement';
import PublicLayout from './public/PublicLayout';
import './AdminDashboard.css';

const AdminDashboard = ({ user, onLogout, defaultTab }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || 'overview');
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    members: { total: 0, active: 0, pending: 0 },
    courses: { total: 0, published: 0 },
    products: { total: 0, active: 0 },
    donors: { total: 0, active: 0 },
    funds: { total: 0, active: 0 },
    events: { total: 0, published: 0 },
    leadership: { total: 0, active: 0 },
    collaborators: { total: 0, active: 0 },
    chapters: { total: 0, active: 0 },
    institutions: { total: 0, active: 0 },
    videos: { total: 0, published: 0 },
    contacts: { new: 0 },
    volunteers: { pending: 0 },
    vendors: { total: 0, pending: 0 }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const isMobile = () => window.innerWidth <= 991;

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    if (isMobile()) {
      setSidebarCollapsed(true);
    }
  };

  useEffect(() => {
    const handleResize = () => setSidebarCollapsed(window.innerWidth <= 991);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Set active tab based on URL hash if present
    const hash = location.hash.replace('#', '');
    if (hash && ['overview', 'members', 'pending-payments', 'courses', 'store', 'events', 'donors', 'users', 'volunteers', 'leadership', 'collaborators', 'chapters', 'institutions', 'videos', 'news', 'community', 'reports', 'contacts', 'bulk-messaging', 'newsletter', 'settings', 'advertisements', 'media-kit', 'photo-gallery', 'impact', 'resources', 'country-flags', 'vendors', 'surveys', 'arca2027-responses', 'cpd-registrations', 'crce-registrations'].includes(hash)) {
      setActiveTab(hash);
    } else if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, location]);

  const renderCpdRegistrations = () => <CpdRegistrationsManagement />;
  const renderCrceRegistrations = () => <CrceRegistrationsManagement />;

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      console.log('🔍 Fetching dashboard data...');
      console.log('🔑 Admin token from localStorage:', token ? `${token.substring(0, 20)}...` : 'NOT FOUND');

      if (!token) {
        console.error('No admin token found. Please log in as an admin.');
        setAuthError(true);
        setLoading(false);
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      console.log('📤 Request headers:', {
        'Authorization': `Bearer ${token.substring(0, 30)}...`,
        'Content-Type': 'application/json'
      });

      // Fetch dashboard overview
      const API_BASE = process.env.REACT_APP_API_URL;

      if (!API_BASE) {
        console.error('❌ CRITICAL: REACT_APP_API_URL not configured!');
        setAuthError(true);
        setLoading(false);
        return;
      }

      console.log('📡 Making request to:', `${API_BASE}/admin/dashboard/overview`);

      const response = await fetch(`${API_BASE}/admin/dashboard/overview`, { headers });

      console.log('📥 Response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Dashboard data received successfully');
        console.log('📊 Full API Response:', JSON.stringify(data, null, 2));
        if (data.success) {
          // Map the API response to our dashboard data structure
          const apiData = data.data.overview;
          console.log('📊 Overview Data:', JSON.stringify(apiData, null, 2));

          setDashboardData(prevData => ({
            ...prevData,
            members: {
              total: apiData.members?.overview?.total_members || 0,
              active: parseInt(apiData.members?.overview?.active_members) || 0,
              pending: parseInt(apiData.members?.overview?.pending_members) || 0
            },
            events: {
              total: apiData.events?.events?.total_events || 0,
              published: apiData.events?.events?.published_events || 0
            },
            leadership: {
              total: apiData.leadership?.total || 0,
              active: apiData.leadership?.active || 0
            },
            chapters: {
              total: apiData.chapters?.total || 0,
              active: apiData.chapters?.active || 0
            },
            videos: {
              total: apiData.videos?.total || 0,
              published: apiData.videos?.published || 0
            },
            courses: {
              total: apiData.courses?.total || 0,
              published: apiData.courses?.published || 0
            },
            products: {
              total: apiData.products?.total || 0,
              active: apiData.products?.active || 0
            },
            collaborators: {
              total: apiData.collaborators?.total || 0,
              active: apiData.collaborators?.active || 0
            },
            contacts: {
              new: apiData.contacts?.new || 0
            },
            volunteers: {
              pending: apiData.volunteers?.pending || 0
            }
          }));
          setAuthError(false);
        }

        // Fetch vendor stats separately
        try {
          const vendorStatsRes = await fetch(`${API_BASE}/vendors/stats`, { headers });
          if (vendorStatsRes.ok) {
            const vendorData = await vendorStatsRes.json();
            if (vendorData.success) {
              setDashboardData(prevData => ({
                ...prevData,
                vendors: {
                  total: vendorData.data.total || 0,
                  pending: vendorData.data.pending || 0
                }
              }));
            }
          }
        } catch (e) {
          // vendor stats fetch failure is non-critical
        }
      } else if (response.status === 401) {
        const errorData = await response.json();
        console.error('❌ Error details:', errorData);

        setAuthError(true);

        // Clear the invalid token and user data
        localStorage.removeItem('adminToken');
        localStorage.removeItem('arcaUser');

        // Show error message
        console.warn('⚠️ Admin token expired or invalid. Redirecting to login...');

        // Redirect to login immediately
        navigate('/login', {
          replace: true,
          state: {
            message: 'Your session has expired. Please log in again.',
            from: location.pathname
          }
        });
        return;
      } else {
        console.error('❌ Unexpected response status:', response.status);
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching dashboard data:', error);
      console.error('❌ Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      // Don't set authError for network errors
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color = '#6B8E23' }) => (
    <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body">
        <div className="d-flex align-items-center">
          <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: `${color}20` }}>
            <span className="fs-4" style={{ color }}>{icon}</span>
          </div>
          <div className="ms-3">
            <p className="text-muted mb-1 small">{title}</p>
            <p className="h4 fw-bold mb-0" style={{ color }}>{value}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOverview = () => (
    <div className="mb-4">
      {/* Welcome Banner */}
      <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #6B8E23 0%, #58761C 100%)' }}>
        <div className="card-body text-white">
          <h2 className="h3 fw-bold mb-2">Welcome, {user?.username || 'Admin'}!</h2>
          <p className="opacity-90 mb-0">Manage your organization's activities and monitor key metrics from here.</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="row g-4 mb-4">
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Total Members"
            value={dashboardData.members?.total || 0}
            icon="👥"
            color="#6B8E23"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Active Members"
            value={dashboardData.members?.active || 0}
            icon="✅"
            color="#4CAF50"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Pending Members"
            value={dashboardData.members?.pending || 0}
            icon="⏳"
            color="#FFC107"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Total Courses"
            value={dashboardData.courses?.total || 0}
            icon="📚"
            color="#2196F3"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Published Courses"
            value={dashboardData.courses?.published || 0}
            icon="📖"
            color="#9C27B0"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Active Products"
            value={dashboardData?.products?.active || 0}
            icon="🛒"
            color="#FF5722"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Leadership Members"
            value={dashboardData?.leadership?.total || 0}
            icon="👔"
            color="#6B8E23"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Collaborators"
            value={dashboardData?.collaborators?.total || 0}
            icon="🤝"
            color="#00BCD4"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="ARCA Chapters"
            value={dashboardData?.chapters?.total || 0}
            icon="🌍"
            color="#4CAF50"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="RT Institutions"
            value={dashboardData?.institutions?.total || 0}
            icon="🏫"
            color="#6B8E23"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="YouTube Videos"
            value={dashboardData?.videos?.total || 0}
            icon="🎥"
            color="#F44336"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Total Events"
            value={dashboardData?.events?.total || 0}
            icon="📅"
            color="#FF9800"
          />
        </div>
        <div className="col-md-6 col-lg-4">
          <StatCard
            title="Vendor Partners"
            value={dashboardData?.vendors?.total || 0}
            icon="🏢"
            color="#795548"
          />
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card border-0 shadow-sm">
        <div className="card-body">
          <h3 className="h5 fw-medium text-dark mb-4">Quick Actions</h3>
          <div className="row g-3">
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('members')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">👥</span>
                  <div>
                    <span className="fw-medium">Manage Members</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('courses')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📚</span>
                  <div>
                    <span className="fw-medium">Manage Courses</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('leadership')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">👔</span>
                  <div>
                    <span className="fw-medium">Leadership</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('chapters')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">🌍</span>
                  <div>
                    <span className="fw-medium">Chapters</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('institutions')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">🏫</span>
                  <div>
                    <span className="fw-medium">RT Institutions</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('collaborators')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">🤝</span>
                  <div>
                    <span className="fw-medium">Collaborators</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('videos')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">🎥</span>
                  <div>
                    <span className="fw-medium">Videos</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('news')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📰</span>
                  <div>
                    <span className="fw-medium">News & Stories</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('store')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">🛒</span>
                  <div>
                    <span className="fw-medium">Manage Store</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('events')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📅</span>
                  <div>
                    <span className="fw-medium">Manage Events</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('community')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">👨‍👩‍👧‍👦</span>
                  <div>
                    <span className="fw-medium">Community</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('reports')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📄</span>
                  <div>
                    <span className="fw-medium">Reports</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('media-kit')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📰</span>
                  <div>
                    <span className="fw-medium">Media Kit</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('photo-gallery')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📸</span>
                  <div>
                    <span className="fw-medium">Photo Gallery</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('contacts')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">📧</span>
                  <div>
                    <span className="fw-medium">Contact Messages</span>
                  </div>
                </div>
              </button>
            </div>
            <div className="col-md-6 col-lg-3">
              <button
                onClick={() => handleTabClick('vendors')}
                className="btn w-100 text-start p-3 border rounded"
                style={{ borderColor: '#dee2e6' }}
              >
                <div className="d-flex align-items-center">
                  <span className="fs-3 me-2">🏢</span>
                  <div>
                    <span className="fw-medium">Vendor Partners</span>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderMembers = () => <MemberManagement />;
  const renderPendingPayments = () => <PendingPaymentsManagement />;

  const renderCourses = () => <SimpleCourseManagement />;

  const renderStore = () => <StoreManagement />;

  const renderEvents = () => <EventManagement />;

  const renderDonors = () => <DonorManagementSystem />;

  const renderUsers = () => <UserManagement />;

  const renderVolunteers = () => <VolunteerApplications user={user} />;

  const renderLeadership = () => <LeadershipManagement />;

  const renderCollaborators = () => <CollaboratorsManagement />;

  const renderChapters = () => <ChaptersManagement />;

  const renderInstitutions = () => <InstitutionsManagement />;

  const renderVideos = () => <YouTubeVideosManagement />;

  const renderNews = () => <NewsManagement />;

  const renderCommunity = () => <CommunityManagement />;

  const renderReports = () => <LeaderReportsManagement />;

  const renderContacts = () => <ContactSubmissions user={user} />;

  const renderBulkMessaging = () => <BulkMessaging />;

  const renderSettings = () => <SettingsManagement />;

  const renderAdvertisements = () => <AdvertisementManagement />;

  const renderMediaKit = () => <MediaKitManagement />;

  const renderPhotoGallery = () => <PhotoGalleryManagement />;

  const renderImpact = () => <ImpactManagement />;
  const renderResources = () => <ResourcesManagement />;
  const renderCountryFlags = () => <CountryFlagsManagement />;
  const renderVendors = () => <VendorManagement />;
  const renderSurveys = () => <SurveyManagement />;
  const renderArca2027Responses = () => <Arca2027SurveyResponses />;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'members': return renderMembers();
      case 'pending-payments': return renderPendingPayments();
      case 'courses': return renderCourses();
      case 'store': return renderStore();
      case 'events': return renderEvents();
      case 'donors': return renderDonors();
      case 'users': return renderUsers();
      case 'volunteers': return renderVolunteers();
      case 'leadership': return renderLeadership();
      case 'collaborators': return renderCollaborators();
      case 'chapters': return renderChapters();
      case 'institutions': return renderInstitutions();
      case 'videos': return renderVideos();
      case 'news': return renderNews();
      case 'community': return renderCommunity();
      case 'reports': return renderReports();
      case 'contacts': return renderContacts();
      case 'bulk-messaging': return renderBulkMessaging();
      case 'newsletter': return <NewsletterManagement />;
      case 'settings': return renderSettings();
      case 'advertisements': return renderAdvertisements();
      case 'media-kit': return renderMediaKit();
      case 'photo-gallery': return renderPhotoGallery();
      case 'impact': return renderImpact();
      case 'resources': return renderResources();
      case 'country-flags': return renderCountryFlags();
      case 'vendors': return renderVendors();
      case 'surveys': return renderSurveys();
      case 'arca2027-responses': return renderArca2027Responses();
      case 'cpd-registrations': return renderCpdRegistrations();
      case 'crce-registrations': return renderCrceRegistrations();
      default: return renderOverview();
    }
  };

  return (
    <PublicLayout user={user} onLogout={onLogout}>
      <div className="admin-dashboard-wrapper">
        {/* Mobile sidebar toggle */}
        <button
          className="admin-mobile-toggle"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label="Toggle admin menu"
        >
          <i className={`bi bi-${sidebarCollapsed ? 'list' : 'x-lg'} fs-4`}></i>
        </button>

        {/* Mobile overlay */}
        <div
          className={`admin-sidebar-overlay ${!sidebarCollapsed ? 'open' : ''}`}
          onClick={() => setSidebarCollapsed(true)}
        />

        {/* Animated Sidebar */}
        <aside className={`admin-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {/* Sidebar Header */}
          <div className="sidebar-header">
            <div className="d-flex align-items-center justify-content-between">
              {!sidebarCollapsed && (
                <div>
                  <h5 className="mb-0 text-white fw-bold">Admin Panel</h5>
                  <small className="text-white-50">Management Dashboard</small>
                </div>
              )}
              <button
                className="btn btn-link text-white p-0"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                <i className={`bi bi-${sidebarCollapsed ? 'chevron-right' : 'chevron-left'} fs-4`}></i>
              </button>
            </div>
          </div>

          {/* Sidebar Navigation */}
          <nav className="sidebar-nav">
            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Main</div>}
              <button
                onClick={() => handleTabClick('overview')}
                className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
                title="Overview"
              >
                <i className="bi bi-speedometer2"></i>
                {!sidebarCollapsed && <span>Overview</span>}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Membership</div>}
              <button
                onClick={() => handleTabClick('members')}
                className={`nav-item ${activeTab === 'members' ? 'active' : ''}`}
                title="Members"
              >
                <i className="bi bi-people"></i>
                {!sidebarCollapsed && <span>Members</span>}
                {!sidebarCollapsed && dashboardData.members.pending > 0 && (
                  <span className="badge bg-warning ms-2">{dashboardData.members.pending}</span>
                )}
              </button>
              <button
                onClick={() => handleTabClick('pending-payments')}
                className={`nav-item ${activeTab === 'pending-payments' ? 'active' : ''}`}
                title="Pending Payments & Activation"
              >
                <i className="bi bi-credit-card"></i>
                {!sidebarCollapsed && <span>Pending Payments</span>}
                {!sidebarCollapsed && dashboardData.members.pending > 0 && (
                  <span className="badge bg-warning ms-2">{dashboardData.members.pending}</span>
                )}
              </button>
              <button
                onClick={() => handleTabClick('users')}
                className={`nav-item ${activeTab === 'users' ? 'active' : ''}`}
                title="Users"
              >
                <i className="bi bi-person"></i>
                {!sidebarCollapsed && <span>Users</span>}
              </button>
              <button
                onClick={() => handleTabClick('volunteers')}
                className={`nav-item ${activeTab === 'volunteers' ? 'active' : ''}`}
                title="Volunteers"
              >
                <i className="bi bi-hand-thumbs-up"></i>
                {!sidebarCollapsed && <span>Volunteers</span>}
                {!sidebarCollapsed && dashboardData.volunteers.pending > 0 && (
                  <span className="badge bg-info ms-2">{dashboardData.volunteers.pending}</span>
                )}
              </button>
              <button
                onClick={() => handleTabClick('vendors')}
                className={`nav-item ${activeTab === 'vendors' ? 'active' : ''}`}
                title="Vendor Partners"
              >
                <i className="bi bi-building"></i>
                {!sidebarCollapsed && <span>Vendor Partners</span>}
                {!sidebarCollapsed && dashboardData.vendors.pending > 0 && (
                  <span className="badge bg-warning ms-2">{dashboardData.vendors.pending}</span>
                )}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Content</div>}
              <button
                onClick={() => handleTabClick('courses')}
                className={`nav-item ${activeTab === 'courses' ? 'active' : ''}`}
                title="Courses"
              >
                <i className="bi bi-book"></i>
                {!sidebarCollapsed && <span>Courses</span>}
              </button>
              <button
                onClick={() => handleTabClick('news')}
                className={`nav-item ${activeTab === 'news' ? 'active' : ''}`}
                title="News"
              >
                <i className="bi bi-newspaper"></i>
                {!sidebarCollapsed && <span>News</span>}
              </button>
              <button
                onClick={() => handleTabClick('videos')}
                className={`nav-item ${activeTab === 'videos' ? 'active' : ''}`}
                title="Videos"
              >
                <i className="bi bi-camera-video"></i>
                {!sidebarCollapsed && <span>Videos</span>}
              </button>
              <button
                onClick={() => handleTabClick('photo-gallery')}
                className={`nav-item ${activeTab === 'photo-gallery' ? 'active' : ''}`}
                title="Photo Gallery"
              >
                <i className="bi bi-images"></i>
                {!sidebarCollapsed && <span>Photo Gallery</span>}
              </button>
              <button
                onClick={() => handleTabClick('media-kit')}
                className={`nav-item ${activeTab === 'media-kit' ? 'active' : ''}`}
                title="Media Kit"
              >
                <i className="bi bi-file-earmark-text"></i>
                {!sidebarCollapsed && <span>Media Kit</span>}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Organization</div>}
              <button
                onClick={() => handleTabClick('leadership')}
                className={`nav-item ${activeTab === 'leadership' ? 'active' : ''}`}
                title="Leadership"
              >
                <i className="bi bi-award"></i>
                {!sidebarCollapsed && <span>Leadership</span>}
              </button>
              <button
                onClick={() => handleTabClick('chapters')}
                className={`nav-item ${activeTab === 'chapters' ? 'active' : ''}`}
                title="Chapters"
              >
                <i className="bi bi-geo-alt"></i>
                {!sidebarCollapsed && <span>Chapters</span>}
              </button>
              <button
                onClick={() => handleTabClick('institutions')}
                className={`nav-item ${activeTab === 'institutions' ? 'active' : ''}`}
                title="RT Institutions"
              >
                <i className="bi bi-building"></i>
                {!sidebarCollapsed && <span>RT Institutions</span>}
              </button>
              <button
                onClick={() => handleTabClick('collaborators')}
                className={`nav-item ${activeTab === 'collaborators' ? 'active' : ''}`}
                title="Collaborators"
              >
                <i className="bi bi-people-fill"></i>
                {!sidebarCollapsed && <span>Collaborators</span>}
              </button>
              <button
                onClick={() => handleTabClick('country-flags')}
                className={`nav-item ${activeTab === 'country-flags' ? 'active' : ''}`}
                title="Country Flags"
              >
                <i className="bi bi-flag-fill"></i>
                {!sidebarCollapsed && <span>Country Flags</span>}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Commerce</div>}
              <button
                onClick={() => handleTabClick('store')}
                className={`nav-item ${activeTab === 'store' ? 'active' : ''}`}
                title="Store"
              >
                <i className="bi bi-shop"></i>
                {!sidebarCollapsed && <span>Store</span>}
              </button>
              <button
                onClick={() => handleTabClick('donors')}
                className={`nav-item ${activeTab === 'donors' ? 'active' : ''}`}
                title="Donors"
              >
                <i className="bi bi-cash-coin"></i>
                {!sidebarCollapsed && <span>Donors</span>}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Engagement</div>}
              <button
                onClick={() => handleTabClick('events')}
                className={`nav-item ${activeTab === 'events' ? 'active' : ''}`}
                title="Events"
              >
                <i className="bi bi-calendar-event"></i>
                {!sidebarCollapsed && <span>Events</span>}
              </button>
              <button
                onClick={() => handleTabClick('community')}
                className={`nav-item ${activeTab === 'community' ? 'active' : ''}`}
                title="Community"
              >
                <i className="bi bi-chat-dots"></i>
                {!sidebarCollapsed && <span>Community</span>}
              </button>
              <button
                onClick={() => handleTabClick('advertisements')}
                className={`nav-item ${activeTab === 'advertisements' ? 'active' : ''}`}
                title="Advertisements"
              >
                <i className="bi bi-megaphone"></i>
                {!sidebarCollapsed && <span>Advertisements</span>}
              </button>
              <button
                onClick={() => handleTabClick('surveys')}
                className={`nav-item ${activeTab === 'surveys' ? 'active' : ''}`}
                title="Surveys"
              >
                <i className="bi bi-clipboard-data"></i>
                {!sidebarCollapsed && <span>Surveys</span>}
              </button>
              <button
                onClick={() => handleTabClick('arca2027-responses')}
                className={`nav-item ${activeTab === 'arca2027-responses' ? 'active' : ''}`}
                title="ARCA 2027 Responses"
              >
                <i className="bi bi-ui-checks-grid"></i>
                {!sidebarCollapsed && <span>ARCA 2027</span>}
              </button>
              <button
                onClick={() => handleTabClick('cpd-registrations')}
                className={`nav-item ${activeTab === 'cpd-registrations' ? 'active' : ''}`}
                title="CPD Registrations"
              >
                <i className="bi bi-mortarboard"></i>
                {!sidebarCollapsed && <span>CPD Registrations</span>}
              </button>
              <button
                onClick={() => handleTabClick('crce-registrations')}
                className={`nav-item ${activeTab === 'crce-registrations' ? 'active' : ''}`}
                title="CRCE Registrations"
              >
                <i className="bi bi-award"></i>
                {!sidebarCollapsed && <span>CRCE Registrations</span>}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">Resources</div>}
              <button
                onClick={() => handleTabClick('impact')}
                className={`nav-item ${activeTab === 'impact' ? 'active' : ''}`}
                title="Impact"
              >
                <i className="bi bi-star"></i>
                {!sidebarCollapsed && <span>Impact</span>}
              </button>
              <button
                onClick={() => handleTabClick('resources')}
                className={`nav-item ${activeTab === 'resources' ? 'active' : ''}`}
                title="Resources"
              >
                <i className="bi bi-folder"></i>
                {!sidebarCollapsed && <span>Resources</span>}
              </button>
              <button
                onClick={() => handleTabClick('reports')}
                className={`nav-item ${activeTab === 'reports' ? 'active' : ''}`}
                title="Reports"
              >
                <i className="bi bi-file-earmark-bar-graph"></i>
                {!sidebarCollapsed && <span>Reports</span>}
              </button>
            </div>

            <div className="nav-section">
              {!sidebarCollapsed && <div className="nav-section-title">System</div>}
              <button
                onClick={() => handleTabClick('contacts')}
                className={`nav-item ${activeTab === 'contacts' ? 'active' : ''}`}
                title="Contact Messages"
              >
                <i className="bi bi-envelope"></i>
                {!sidebarCollapsed && <span>Contact Messages</span>}
                {!sidebarCollapsed && dashboardData.contacts.new > 0 && (
                  <span className="badge bg-danger ms-2">{dashboardData.contacts.new}</span>
                )}
              </button>
              <button
                onClick={() => handleTabClick('bulk-messaging')}
                className={`nav-item ${activeTab === 'bulk-messaging' ? 'active' : ''}`}
                title="Bulk Messaging"
              >
                <i className="bi bi-chat-dots"></i>
                {!sidebarCollapsed && <span>Bulk Messaging</span>}
              </button>
              <button
                onClick={() => handleTabClick('newsletter')}
                className={`nav-item ${activeTab === 'newsletter' ? 'active' : ''}`}
                title="Newsletter"
              >
                <i className="bi bi-newspaper"></i>
                {!sidebarCollapsed && <span>Newsletter</span>}
              </button>
              <button
                onClick={() => handleTabClick('settings')}
                className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
                title="Settings"
              >
                <i className="bi bi-gear"></i>
                {!sidebarCollapsed && <span>Settings</span>}
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main-content">
          {/* Authentication Error Alert */}
          {authError && (
            <div className="alert alert-danger alert-dismissible fade show animate-slide-down" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <strong>Authentication Error:</strong> Your admin session has expired or is invalid.
              You will be redirected to the login page shortly. Please log in with your admin credentials.
              <button type="button" className="btn-close" onClick={() => setAuthError(false)} aria-label="Close"></button>
            </div>
          )}

          {/* Content Area with animation */}
          <div className="content-wrapper animate-fade-in">
            {renderTabContent()}
          </div>
        </main>
      </div>
    </PublicLayout>
  );
};

export default AdminDashboard;